<template>
  <b-modal :active="isComponentModalActive" has-modal-card :can-cancel="false">
    <form class="modal-card survey-modal" @submit.prevent="saveAnswer">
      <header class="modal-card-head survey-header">
        <div class="survey-header-content">
          <span class="survey-eyebrow">Responder encuesta</span>
          <h2 class="modal-card-title">{{ questionaryData.name }}</h2>
          <p>{{ answeredCount }} de {{ Questions.length }} preguntas respondidas</p>
          <div class="survey-progress" role="progressbar" :aria-valuenow="answeredCount" aria-valuemin="0" :aria-valuemax="Questions.length" aria-label="Preguntas respondidas">
            <span :style="{ width: progressPercent + '%' }"></span>
          </div>
        </div>
      </header>

      <section class="modal-card-body survey-body">
        <div v-if="questionaryData.description" class="survey-description" v-html="questionaryData.description"></div>
        <p class="survey-instruction">Todas las preguntas son obligatorias. Responde cada una antes de enviar la encuesta.</p>

        <div v-if="Questions.length === 0" class="survey-message">Esta encuesta aún no tiene preguntas.</div>
        <div v-for="(question, index) in Questions" :key="question.id" class="survey-question-group">
          <h3 v-if="sectionTitle(question.id)" class="survey-section-heading">{{ sectionTitle(question.id) }}</h3>
          <section
            :id="'survey-question-' + question.id"
            class="survey-question"
            :class="{ 'has-validation-error': validationQuestionId === question.id }"
            :aria-labelledby="'survey-question-title-' + question.id"
          >
            <div class="survey-question-heading">
              <span class="survey-question-number">{{ index + 1 }}</span>
              <div class="survey-question-title">
                <div :id="'survey-question-title-' + question.id" class="survey-question-text" v-html="question.question"></div>
                <span>Obligatoria · {{ question.type_selection_id == 1 ? 'Selecciona una opción' : 'Selecciona entre 1 y 6 opciones' }}</span>
              </div>
            </div>
            <div class="survey-options">
              <template v-for="answer in visibleAlternatives(question)">
                <label v-if="question.type_selection_id == 1" :key="answer.alternative.id" class="survey-option" :class="{ selected: question.id_answer === answer.alternative.id, disabled: inputsDisabled }">
                  <input v-model="question.id_answer" type="radio" :name="'question-' + question.id" :value="answer.alternative.id" :disabled="inputsDisabled" @change="clearQuestionError(question.id)" />
                  <span>{{ answer.alternative.alternative }}</span>
                </label>
                <label v-else :key="answer.alternative.id" class="survey-option" :class="{ selected: question.answers.includes(answer.alternative.id), disabled: inputsDisabled || (question.answers.length >= 6 && !question.answers.includes(answer.alternative.id)) }">
                  <input v-model="question.answers" type="checkbox" :value="answer.alternative.id" :disabled="inputsDisabled || (question.answers.length >= 6 && !question.answers.includes(answer.alternative.id))" @change="clearQuestionError(question.id)" />
                  <span>{{ answer.alternative.alternative }}</span>
                </label>
              </template>
            </div>
            <span v-if="question.type_selection_id != 1" class="survey-selection-count">{{ question.answers.length }} de 6 opciones seleccionadas</span>
            <p v-if="validationQuestionId === question.id" class="survey-inline-error" role="alert">{{ validationError }}</p>
          </section>
        </div>

        <div v-if="saveError" class="survey-message survey-error" role="alert">{{ saveError }}</div>
      </section>

      <footer class="modal-card-foot survey-footer">
        <span>{{ answeredCount === Questions.length ? 'Revisa tus respuestas antes de enviarlas.' : (Questions.length - answeredCount) + ' preguntas pendientes de respuesta.' }}</span>
        <div class="survey-footer-actions">
          <button class="button" type="button" :disabled="isSaving" @click="requestClose">Cerrar</button>
          <button class="button is-primary" type="submit" :disabled="isSaving || Questions.length === 0">
            {{ isSaving ? 'Enviando...' : submissionId ? 'Reintentar envío' : 'Enviar respuestas' }}
          </button>
        </div>
      </footer>
    </form>
  </b-modal>
</template>

<script>
import { orderSurveyQuestions } from "../../utils/questionaryOrder";

const SECTION_TITLES = {
  1: "SECCIÓN A: INFORMACIÓN GENERAL",
  4: "SECCIÓN B: POLÍTICA DE GESTIÓN DE PERSONAS",
  8: "SECCIÓN C: DERECHOS Y DEBERES FUNCIONARIOS",
  13: "SECCIÓN D: DECLARACIÓN DE CONOCIMIENTO",
  15: "SECCIÓN E: VALIDACIÓN DE CONOCIMIENTOS",
};

export default {
  props: ["isComponentModalActive", "closeBasic", "questionaryData", "chargeQuestionarieList"],
  data() {
    return {
      Questions: [],
      isSaving: false,
      submissionId: null,
      answerQueue: [],
      sentAnswerIndexes: [],
      validationError: "",
      validationQuestionId: null,
      saveError: "",
    };
  },
  computed: {
    answeredCount() {
      return this.Questions.filter(question => question.type_selection_id == 1 ? question.id_answer !== null : question.answers.length > 0).length;
    },
    progressPercent() {
      return this.Questions.length ? Math.round(this.answeredCount / this.Questions.length * 100) : 0;
    },
    inputsDisabled() {
      return this.isSaving || this.submissionId !== null;
    },
  },
  created() {
    this.Questions = orderSurveyQuestions(this.questionaryData.questions)
      .map(question => ({ ...question, id_answer: null, answers: [] }));
  },
  methods: {
    sectionTitle(questionId) {
      return SECTION_TITLES[Number(questionId)] || "";
    },
    visibleAlternatives(question) {
      return (question.questions_alternatives || []).filter(item => item.alternative && item.alternative.alternative !== "En Blanco");
    },
    clearQuestionError(questionId) {
      if (this.validationQuestionId === questionId) {
        this.validationQuestionId = null;
        this.validationError = "";
      }
    },
    prepareAnswers() {
      const queue = [];
      this.validationError = "";
      this.validationQuestionId = null;

      for (let index = 0; index < this.Questions.length; index++) {
        const question = this.Questions[index];
        const selected = question.type_selection_id == 1
          ? (question.id_answer !== null ? [question.id_answer] : [])
          : question.answers;

        if (!selected.length) {
          this.validationQuestionId = question.id;
          this.validationError = `Responde la pregunta ${index + 1} antes de enviar.`;
          this.$nextTick(() => {
            const element = document.getElementById(`survey-question-${question.id}`);
            if (element) element.scrollIntoView({ behavior: "smooth", block: "center" });
          });
          return null;
        }
        if (selected.length > 6) {
          this.validationQuestionId = question.id;
          this.validationError = `La pregunta ${index + 1} permite hasta 6 opciones.`;
          return null;
        }
        selected.forEach(alternativeId => queue.push(alternativeId));
      }
      return queue;
    },
    async saveAnswer() {
      if (this.isSaving || this.Questions.length === 0) return;
      if (!this.submissionId) {
        const answers = this.prepareAnswers();
        if (!answers) return;
        this.answerQueue = answers;
      }

      this.isSaving = true;
      this.saveError = "";
      try {
        if (!this.submissionId) {
          const user = JSON.parse(window.localStorage.getItem("user") || "{}");
          const response = await this.$http.post(`api/${localStorage.getItem("perfil")}/user_questionary`, {
            questionary_id: this.questionaryData.id,
            user_id: user.id,
            state: "Realizado",
          });
          this.submissionId = response.body.user && response.body.user.id;
          if (!this.submissionId) throw new Error("No se recibió el registro de la encuesta.");
        }

        for (let index = 0; index < this.answerQueue.length; index++) {
          if (this.sentAnswerIndexes.includes(index)) continue;
          await this.$http.post(`api/${localStorage.getItem("perfil")}/answer`, {
            user_questionary_id: this.submissionId,
            alternative_id: this.answerQueue[index],
            state: "Respondido",
          });
          this.sentAnswerIndexes.push(index);
        }
        await this.chargeQuestionarieList();
        this.toast("Encuesta respondida correctamente", "is-success");
        this.closeBasic();
      } catch (error) {
        this.saveError = this.submissionId
          ? "No se pudieron guardar todas las respuestas. Reintenta el envío; se conservarán las que ya se guardaron."
          : "No se pudo iniciar el envío. Revisa tu conexión e inténtalo nuevamente.";
      } finally {
        this.isSaving = false;
      }
    },
    requestClose() {
      if (this.submissionId && this.sentAnswerIndexes.length < this.answerQueue.length) {
        this.$buefy.dialog.confirm({
          title: "Envío incompleto",
          message: "Algunas respuestas aún no se guardaron. Si cierras, tendrás que contactar al administrador para completar esta encuesta.",
          cancelText: "Seguir aquí",
          confirmText: "Cerrar de todos modos",
          type: "is-warning",
          onConfirm: this.closeBasic,
        });
        return;
      }
      this.closeBasic();
    },
  },
};
</script>

<style scoped>
.survey-modal { width: min(94vw, 760px); max-height: calc(100vh - 3rem); border-radius: 16px; overflow: hidden; }
.survey-header { padding: 1.4rem 1.8rem; background: #f4faf7; }
.survey-header-content { width: 100%; }
.survey-eyebrow { color: #187466; font-size: .74rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.survey-header .modal-card-title { margin: .35rem 0; color: #23464c; font-size: clamp(1.3rem, 3vw, 1.8rem); font-weight: 700; line-height: 1.25; white-space: normal; }
.survey-header p { color: #627a7b; font-size: .87rem; }
.survey-progress { height: .48rem; margin-top: .85rem; border-radius: 99px; background: #dcebe5; overflow: hidden; }
.survey-progress span { display: block; height: 100%; border-radius: inherit; background: #188675; transition: width .2s ease; }
.survey-body { padding: 1.5rem 1.8rem; background: #fbfdfc; }
.survey-description { margin-bottom: 1rem; padding: 1rem; border-left: 3px solid #64aa94; border-radius: 0 8px 8px 0; background: #edf7f2; color: #315961; line-height: 1.5; }
.survey-instruction { margin-bottom: 1.4rem; color: #62777b; font-size: .89rem; line-height: 1.5; }
.survey-section-heading { margin: 1.5rem 0 .7rem; padding: .7rem 1rem; border-left: 4px solid #198472; border-radius: 0 8px 8px 0; background: #eaf5ef; color: #176151; font-size: 1.1rem; font-weight: 700; }
.survey-question { margin-bottom: 1rem; padding: 1.3rem; border: 1px solid #e0eae7; border-radius: 13px; background: white; }
.survey-question.has-validation-error { border-color: #cf6d5b; }
.survey-question-heading { display: flex; align-items: flex-start; gap: .8rem; margin-bottom: 1rem; }
.survey-question-number { display: grid; flex: none; place-items: center; width: 2rem; height: 2rem; border-radius: 9px; background: #e7f4ed; color: #18735a; font-weight: 700; }
.survey-question-title { min-width: 0; }
.survey-question-text { color: #25464c; font-size: 1rem; font-weight: 700; line-height: 1.4; }
.survey-question-title > span { color: #748689; font-size: .79rem; }
.survey-options { display: grid; gap: .55rem; }
.survey-option { display: flex; align-items: flex-start; gap: .75rem; padding: .85rem 1rem; border: 1px solid #dce7e5; border-radius: 9px; color: #3b5960; cursor: pointer; line-height: 1.4; }
.survey-option:hover { border-color: #8cc8b5; background: #f7fbf8; }
.survey-option.selected { border-color: #32957a; background: #edf8f2; }
.survey-option.disabled { cursor: default; opacity: .65; }
.survey-option input { flex: none; margin-top: .18rem; accent-color: #198472; }
.survey-selection-count { display: inline-block; margin-top: .55rem; color: #718487; font-size: .76rem; }
.survey-inline-error { margin-top: .65rem; color: #a53b31; font-size: .86rem; font-weight: 600; }
.survey-message { padding: 1rem; border-radius: 8px; background: #edf3f1; color: #4b686c; }
.survey-error { margin-top: .75rem; background: #fff0ec; color: #a53b31; }
.survey-footer { display: flex; justify-content: space-between; gap: 1rem; background: white; }
.survey-footer > span { color: #748689; font-size: .81rem; }
.survey-footer-actions { display: flex; flex: none; gap: .6rem; }
.survey-footer-actions .button { border-radius: 8px; font-weight: 700; }
@media (max-width: 600px) {
  .survey-header, .survey-body, .survey-footer { padding: 1rem; }
  .survey-footer { flex-direction: column; align-items: stretch; }
  .survey-footer-actions { display: grid; grid-template-columns: 1fr 1fr; }
  .survey-footer-actions .button { white-space: normal; height: auto; min-height: 2.7rem; }
}
</style>
